import { getToken, ensureFreshToken, clearTokens } from '@/state/auth'
import { APP_NAME, ORG_ID } from './utils'

const config = {
  baseUrl: '',
  organizationId: -1
}

type RequestOptions = {
  method?: string
  body?: string
  headers?: Record<string, string>
  refreshing?: boolean
}

type PaginationOptions = {
  current: number
  limit: number
}

function init(baseUrl: string, organizationId: number) {
  config.baseUrl = baseUrl
  config.organizationId = organizationId
}

async function request(
  url: string,
  data?: Record<string, unknown>,
  options?: RequestOptions
) {
  const params = {
    method: 'GET',
    ...options
  }

  params.headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'Sites-Organization-Id': String(config.organizationId),
    ...params.headers
  }
  let token
  if (options?.refreshing) {
    token = getToken()
  } else {
    token = getToken() ? await ensureFreshToken() : null
  }
  if (token) {
    params.headers.Authorization = 'Bearer ' + token
  }

  let query = ''
  if (data && params.method.toUpperCase() === 'GET') {
    const stringifiedData = Object.keys(data).reduce(
      (acc: Record<string, string>, i) => ({ ...acc, [i]: String(data[i]) }),
      {}
    )
    query = '?' + new URLSearchParams(stringifiedData)
  } else if (data) {
    params.body = JSON.stringify(data)
  }

  try {
    const response = await fetch(
      `${config.baseUrl}/api/v2/${url}${query}`,
      params
    )
    const json = await response.json()
    if (json?.message?.startsWith('Unauthenticated.')) {
      clearTokens()
      // @todo: here we want to store their current changes in localStorage, redirect them to sso authenticate, and then load their changes back into the page when they return - BM
      window.location.reload()
    }
    return json
  } catch (e) {
    throw new Error(`API request failed: ${e}`)
  }
}

async function getTokens(code: string) {
  const data = await request('sso/token', { code }, { method: 'POST' })
  const value = data as {
    access_token: string
    refresh_token: string
  }
  const accessToken = value?.access_token
  const refreshToken = value?.refresh_token
  return { accessToken, refreshToken }
}

async function refreshToken(token: string) {
  const data = await request(
    'sso/refresh-token',
    { refresh_token: token },
    { method: 'POST', refreshing: true }
  )
  const value = data as {
    access_token: string
    refresh_token: string
  }
  const accessToken = value?.access_token
  const refreshToken = value?.refresh_token

  if (!accessToken || !refreshToken) {
    throw new Error('Unable to refresh token.')
  }

  return { accessToken, refreshToken }
}

async function getOrganization(organizationId: number = +ORG_ID) {
  const data = await request(`organizations/${organizationId}`)
  return data
}

/** Fetch the current page data from the server */
async function getPage(url?: string) {
  const slug = url || window.location.pathname
  const { exists, page } = await request('pages', { slug })

  if (!exists) throw new Error(`Page is not managed by ${APP_NAME}.`)

  return page
}

/** Fetch the current page data from the server. */
async function getPageById(id: number) {
  const { exists, page } = await request(`pages/${id}`)

  if (!exists) throw new Error(`Page is not managed by ${APP_NAME}.`)

  return page
}

async function getPages() {
  const { data } = await request('pages', { limit: 10 })
  return data
}

/** Get the types of pages allowed and their stripe configurations. */
async function getTypes() {
  const { data } = await request('types')
  return data
}

/** Create a new page */
async function createPage(page: Record<string, unknown>) {
  const { data } = await request('pages', page, { method: 'POST' })
  return data
}

/** Duplicate a new page */
async function clonePage(values: Record<string, unknown>) {
  const { data } = await request(`pages/${values.id}/clone`, values, {
    method: 'POST'
  })
  return data
}

/** Update an existing page. */
async function updatePage(page: Record<string, unknown>) {
  const data = await request(`pages/${page.id}`, page, { method: 'PUT' })
  return data
}

/** Delete a particular page. */
async function deletePage(pageId: number) {
  const { data } = await request(`pages/${pageId}`, {}, { method: 'DELETE' })
  return data
}

/** Search the organization's pages by term (title only) */
async function searchPages(term: string) {
  const { data } = await request('pages', { term })
  return data
}

/** Get the menus for this site. */
async function getMenus() {
  const data = await request('menus')
  return data
}

/** Get a single menu with links for this site. */
async function getMenu(id: number) {
  const data = await request(`menus/${id}`)
  return data
}

/** Update a menu via api. */
async function updateMenu(menu: Record<string, unknown>) {
  const { data } = await request(`menus/${menu.id}`, menu, { method: 'PUT' })
  return data
}

/** Get OG information for a link. */
async function importLink(url: string) {
  const data = await request('import', { url }, { method: 'POST' })
  return data
}

/** Get your own user data. */
async function getSelf() {
  const data = await request('sso/user')
  return data
}

/** Get a listing of all the users that belong to this organization. */
async function getUsers(pagination: PaginationOptions) {
  const { current, limit } = pagination
  const page = current > 0 ? current - 1 : 0
  const start = page * limit
  const { data } = await request('users', { limit, start })
  return data
}

export default {
  init,
  getTokens,
  refreshToken,
  getOrganization,
  getPage,
  getPageById,
  getPages,
  getTypes,
  createPage,
  clonePage,
  updatePage,
  deletePage,
  searchPages,
  getMenus,
  getMenu,
  updateMenu,
  importLink,
  getSelf,
  getUsers
}
